import { filterMenuForPos } from './pos.util';
import { filterMenuByRolePaths } from './rbac-routes.util';

/** Routes that should highlight a different nav item than their URL prefix. */
const MENU_ROUTE_ALIASES: Record<string, string> = {
  '/customer-analytics': '/members',
  '/loyalty/create': '/loyalty-programs',
  // Timed sequences live under Automations — keep the parent highlighted.
  '/journeys': '/rules',
};

export interface SideMenuItem {
  src: string;
  icon?: string;
  name: string;
  path: string;
  badge?: string;
}

export interface SideMenuSection {
  label: string;
  items: SideMenuItem[];
}

export interface MobileMoreMenuItem extends SideMenuItem {
  description?: string;
}

export const ALL_SIDE_MENU_SECTIONS: SideMenuSection[] = [
  {
    label: 'Grow',
    items: [
      { src: '/icons/side-menu/Dashboard.png', icon: 'layout-dashboard', name: 'Dashboard', path: '/dashboard' },
      { src: '/icons/side-menu/Dashboard.png', icon: 'rocket', name: 'Get Started', path: '/get-started' },
      { src: '/icons/side-menu/Analytics & Reports.png', icon: 'chart-no-axes-combined', name: 'Analytics', path: '/analytics' },
      { src: '/icons/side-menu/Analytics & Reports.png', icon: 'refresh-cw', name: 'Retention', path: '/analytics/retention' },
      { src: '/icons/side-menu/Campaigns.png', icon: 'send', name: 'Campaigns', path: '/campaigns' },
      { src: '/icons/side-menu/referral.png', icon: 'git-branch', name: 'Referrals', path: '/referral' },
    ],
  },
  {
    label: 'Engage',
    items: [
      { src: '/icons/side-menu/Loyalty Programs.png', icon: 'award', name: 'Loyalty', path: '/loyalty-programs' },
      // { src: '/icons/side-menu/Loyalty Programs.png', icon: 'crown', name: 'Tiers', path: '/tiers' },
      { src: '/icons/side-menu/Rewards.png', icon: 'gift', name: 'Rewards', path: '/rewards' },
      { src: '/icons/side-menu/coupon.png', icon: 'ticket', name: 'Coupons', path: '/coupon' },
      // { src: '/icons/side-menu/coupon.png', icon: 'scan-line', name: 'QR Codes', path: '/qr-codes' },
      { src: '/icons/side-menu/Campaigns.png', icon: 'megaphone', name: 'Announcements', path: '/announcements' },
      { src: '/icons/side-menu/Feedback.png', icon: 'help-circle', name: 'FAQs', path: '/faq' },
      { src: '/icons/side-menu/Campaigns.png', icon: 'calendar', name: 'Festival calendar', path: '/festival-calendar' },
      { src: '/icons/side-menu/Members.png', icon: 'smartphone', name: 'Member Portal', path: '/member-portal' },
      // { src: '/icons/side-menu/Transactions.png', icon: 'banknote', name: 'Payouts', path: '/payouts' },
      { src: '/icons/side-menu/rule.png', icon: 'workflow', name: 'Automations', path: '/rules' },
      { src: '/icons/settings/settings.png', icon: 'shield', name: 'Policies', path: '/policies' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { src: '/icons/side-menu/Members.png', icon: 'users', name: 'Members', path: '/members' },
      { src: '/icons/side-menu/Segments.png', icon: 'filter', name: 'Segments', path: '/segments' },
      { src: '/icons/side-menu/product.png', icon: 'package', name: 'Products', path: '/products' },
      { src: '/icons/side-menu/Transactions.png', icon: 'receipt', name: 'Transactions', path: '/transactions' },
      { src: '/icons/side-menu/Feedback.png', icon: 'message-square', name: 'Feedback', path: '/feedback' },
    ],
  },
  {
    label: 'Connect',
    items: [
      { src: '/icons/side-menu/integration.png', icon: 'plug', name: 'Integrations', path: '/integration' },
      // { src: '/icons/side-menu/integration.png', icon: 'message-circle', name: 'Channels', path: '/channels' },
      { src: '/icons/settings/Web hook.png', icon: 'code-2', name: 'API & Webhooks', path: '/api-webhooks' },
      { src: '/icons/side-menu/rule.png', icon: 'zap', name: 'Events', path: '/events' },
      { src: '/icons/home/pay.png', icon: 'credit-card', name: 'Plans & Billing', path: '/billing' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { src: '/icons/settings/settings.png', icon: 'building', name: 'Business', path: '/settings/business' },
      { src: '/icons/side-menu/Members.png', icon: 'users', name: 'Team', path: '/settings/team' },
      // { src: '/icons/side-menu/Transactions.png', icon: 'scroll-text', name: 'Audit log', path: '/settings/audit' },
      { src: '/icons/settings/Customize fields.png', icon: 'file-text', name: 'Custom Fields', path: '/settings/custom-fields' },
      { src: '/icons/side-menu/Dashboard.png', icon: 'image', name: 'Media', path: '/settings/media' },
      { src: '/icons/side-menu/Dashboard.png', icon: 'gauge', name: 'Setup Progress', path: '/settings/setup' },
      { src: '/icons/side-menu/Feedback.png', icon: 'book-marked', name: 'How It Works', path: '/how-it-works' },
    ],
  },
];

const MOBILE_MORE_DESCRIPTIONS: Record<string, string> = {
  '/analytics': 'Outcome KPIs and trends',
  '/analytics/retention': 'Churn, CLV and repeat purchase',
  '/campaigns': 'Growth campaigns',
  '/referral': 'Referral programs',
  '/tiers': 'Status ladder and VIP tiers',
  '/coupon': 'Manage coupons',
  '/qr-codes': 'Product QR loyalty codes',
  '/announcements': 'Portal banners, promos & news',
  '/faq': 'Member help questions & answers',
  '/festival-calendar': 'Seasonal windows & campaign starters',
  '/member-portal': 'Guest pages & branded app — list then customize',
  '/member-portal/engagement': 'Portal sign-ins & widget analytics',
  '/rules': 'Automations and rules',
  '/policies': 'Messaging, quiet hours & related rules',
  '/redemption': 'Redemption rules',
  '/segments': 'Customer segmentation',
  '/products': 'Menu metrics & catalog',
  '/transactions': 'Transaction history',
  '/feedback': 'Customer reviews',
  '/integration': 'POS and integrations',
  '/billing': 'Plans and billing',
  '/api-webhooks': 'API keys and webhooks',
  '/events': 'Event catalogue, custom events & logs',
  '/settings/business': 'Organization details',
  '/settings/team': 'Users and roles',
  '/settings/audit': 'Who changed what',
  '/settings/outlets': 'Outlets',
  '/settings/custom-fields': 'CRM custom fields',
  '/settings/media': 'Reusable images, videos & files',
  '/settings/setup': 'Onboarding progress',
  '/how-it-works': 'Guide, examples & best practices',
};

/** Normalize router URL (strip query + matrix params). */
export function normalizeRouteUrl(url: string): string {
  return url.split('?')[0].split(';')[0];
}

/**
 * Resolve which sidebar path should appear active for the current URL.
 * Supports exact matches, nested child routes, and explicit aliases.
 */
export function resolveActiveMenuPath(url: string, menuPaths: string[]): string {
  let path = normalizeRouteUrl(url);
  if (MENU_ROUTE_ALIASES[path]) {
    path = MENU_ROUTE_ALIASES[path];
  } else {
    const aliasPrefix = Object.keys(MENU_ROUTE_ALIASES).find((key) => path.startsWith(`${key}/`));
    if (aliasPrefix) {
      path = MENU_ROUTE_ALIASES[aliasPrefix];
    }
  }
  if (menuPaths.includes(path)) {
    return path;
  }

  const sorted = [...menuPaths].sort((a, b) => b.length - a.length);
  for (const menuPath of sorted) {
    if (path.startsWith(`${menuPath}/`)) {
      return menuPath;
    }
  }

  return '';
}

export function isMenuItemActive(
  itemPath: string,
  currentUrl: string,
  menuPaths: string[],
): boolean {
  return resolveActiveMenuPath(currentUrl, menuPaths) === itemPath;
}

export function cloneSideMenuSections(
  sections: SideMenuSection[] = ALL_SIDE_MENU_SECTIONS,
): SideMenuSection[] {
  return sections.map((section) => ({
    ...section,
    items: [...section.items],
  }));
}

export function filterSideMenuSections(
  sections: SideMenuSection[],
  posName?: string | null,
  allowedPaths?: readonly string[] | null,
  role?: string | null,
): SideMenuSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: filterMenuByRolePaths(
        filterMenuForPos(section.items, posName),
        allowedPaths,
        role,
      ),
    }))
    .filter((section) => section.items.length > 0);
}

export function flattenSideMenuSections(sections: SideMenuSection[]): SideMenuItem[] {
  return sections.flatMap((section) => section.items);
}

export function buildMobileMoreMenuSections(
  sections: SideMenuSection[],
  mainNavPaths: string[],
  posName?: string | null,
  allowedPaths?: readonly string[] | null,
  role?: string | null,
): SideMenuSection[] {
  const mainPaths = new Set(mainNavPaths);

  return filterSideMenuSections(
    sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => !mainPaths.has(item.path)),
    })),
    posName,
    allowedPaths,
    role,
  );
}

export function toMobileMoreMenuItem(item: SideMenuItem): MobileMoreMenuItem {
  return {
    ...item,
    description: MOBILE_MORE_DESCRIPTIONS[item.path],
  };
}
